/**
 * 프롬프트 템플릿 생성/편집/클론 모달 (M14 / F-3) — admin 전용.
 *
 * 모드:
 *   - create : 빈 본문에서 새 draft 생성.
 *   - edit   : draft 행 본문 수정 (PATCH).
 *   - clone  : 기존(active/archived) 본문을 복제해 새 draft 생성 (parent_id 표시).
 *
 * 안전장치: user_prompt_template 에 필수 placeholder 3종이 모두 포함돼야 저장 가능
 * (라이브 검증 — 누락 토큰을 빨간 칩으로 안내).
 */

import { useMemo, useRef, useState } from "react";
import { Download, HelpCircle, Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";
import {
  findMissingPlaceholders,
  getPromptDefaults,
  REQUIRED_PROMPT_PLACEHOLDERS,
  type PromptTemplateOut,
} from "@/lib/api/prompts";

import { useCreatePromptDraft, useUpdatePromptDraft } from "./usePrompts";

type Mode = "create" | "edit" | "clone";

interface Props {
  mode: Mode;
  /** edit / clone 시 원본. */
  prompt?: PromptTemplateOut;
  onClose: () => void;
}

export default function PromptEditorModal({ mode, prompt, onClose }: Props) {
  const { t } = useTranslation();
  const createMu = useCreatePromptDraft();
  const updateMu = useUpdatePromptDraft();

  const [systemPrompt, setSystemPrompt] = useState(prompt?.system_prompt ?? "");
  const [userPrompt, setUserPrompt] = useState(prompt?.user_prompt_template ?? "");
  const [notes, setNotes] = useState(mode === "edit" ? (prompt?.notes ?? "") : "");
  const [showHelp, setShowHelp] = useState(mode === "create");
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const userRef = useRef<HTMLTextAreaElement>(null);

  const mutation = mode === "edit" ? updateMu : createMu;

  // 기본 템플릿 불러오기 — 빈 초안 시작점.
  const handleLoadDefaults = async () => {
    if (
      (systemPrompt.trim() || userPrompt.trim()) &&
      !window.confirm(t("admin.prompts.editor.loadDefaultsConfirm"))
    ) {
      return;
    }
    setLoadingDefaults(true);
    try {
      const d = await getPromptDefaults();
      setSystemPrompt(d.system_prompt);
      setUserPrompt(d.user_prompt_template);
    } finally {
      setLoadingDefaults(false);
    }
  };

  // 토큰을 user 본문의 커서 위치에 삽입.
  const insertToken = (token: string) => {
    const el = userRef.current;
    if (!el) {
      setUserPrompt((v) => v + token);
      return;
    }
    const start = el.selectionStart ?? userPrompt.length;
    const end = el.selectionEnd ?? userPrompt.length;
    const next = userPrompt.slice(0, start) + token + userPrompt.slice(end);
    setUserPrompt(next);
    // 삽입 후 커서를 토큰 뒤로
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const missing = useMemo(() => findMissingPlaceholders(userPrompt), [userPrompt]);
  const formValid =
    systemPrompt.trim().length > 0 && userPrompt.trim().length > 0 && missing.length === 0;
  const canSubmit = formValid && !mutation.isPending;

  const apiError =
    mutation.error instanceof ApiError ? mutation.error.detail : (mutation.error?.message ?? null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    if (mode === "edit" && prompt) {
      updateMu.mutate(
        {
          id: prompt.id,
          payload: {
            system_prompt: systemPrompt,
            user_prompt_template: userPrompt,
            notes: notes.trim() || null,
          },
        },
        { onSuccess: () => onClose() },
      );
    } else {
      // create / clone — clone 은 parent_id 만 표시용으로 전달.
      createMu.mutate(
        {
          name: prompt?.name,
          system_prompt: systemPrompt,
          user_prompt_template: userPrompt,
          notes: notes.trim() || null,
          parent_id: mode === "clone" ? (prompt?.id ?? null) : null,
        },
        { onSuccess: () => onClose() },
      );
    }
  };

  const title =
    mode === "edit"
      ? t("admin.prompts.editor.editTitle", { version: prompt?.version })
      : mode === "clone"
        ? t("admin.prompts.editor.cloneTitle", { version: prompt?.version })
        : t("admin.prompts.editor.createTitle");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 py-8"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setShowHelp((v) => !v)}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[11px] text-slate-600 transition hover:bg-slate-50"
            >
              <HelpCircle className="h-3.5 w-3.5" />
              {t("admin.prompts.editor.helpToggle")}
            </button>
            <button
              type="button"
              onClick={handleLoadDefaults}
              disabled={loadingDefaults}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[11px] text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
            >
              {loadingDefaults ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
              {t("admin.prompts.editor.loadDefaults")}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label={t("common.actions.close")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
            {/* 인라인 도움말 */}
            {showHelp && (
              <div className="space-y-1.5 rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 text-[11px] leading-relaxed text-slate-700">
                <p className="font-medium text-blue-800">{t("admin.prompts.editor.help.title")}</p>
                <p>{t("admin.prompts.editor.help.flow")}</p>
                <p>{t("admin.prompts.editor.help.system")}</p>
                <p>{t("admin.prompts.editor.help.user")}</p>
                <p>{t("admin.prompts.editor.help.tokens")}</p>
                <p>{t("admin.prompts.editor.help.json")}</p>
                <p>{t("admin.prompts.editor.help.ocr")}</p>
                <p className="text-blue-700">{t("admin.prompts.editor.help.tip")}</p>
              </div>
            )}
            {/* system prompt */}
            <div>
              <label htmlFor="pe-system" className="block text-xs font-medium text-slate-700">
                {t("admin.prompts.editor.systemLabel")}
              </label>
              <textarea
                id="pe-system"
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                rows={5}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                disabled={mutation.isPending}
              />
            </div>

            {/* user prompt template */}
            <div>
              <label htmlFor="pe-user" className="block text-xs font-medium text-slate-700">
                {t("admin.prompts.editor.userLabel")}
              </label>
              <p className="mt-0.5 text-[11px] text-slate-400">
                {t("admin.prompts.editor.placeholderHint")}
              </p>
              {/* 토큰 삽입 버튼 */}
              <div className="mt-1 flex flex-wrap items-center gap-1">
                <span className="text-[10px] text-slate-400">
                  {t("admin.prompts.editor.insertToken")}
                </span>
                {REQUIRED_PROMPT_PLACEHOLDERS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => insertToken(p)}
                    className="rounded border border-slate-300 px-1.5 py-0.5 font-mono text-[10px] text-slate-600 transition hover:bg-slate-100"
                  >
                    {p}
                  </button>
                ))}
              </div>
              <textarea
                ref={userRef}
                id="pe-user"
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                rows={10}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                disabled={mutation.isPending}
              />
              {/* placeholder 상태 칩 */}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {REQUIRED_PROMPT_PLACEHOLDERS.map((p) => {
                  const ok = !missing.includes(p);
                  return (
                    <span
                      key={p}
                      className={
                        ok
                          ? "rounded-full bg-emerald-100 px-2 py-0.5 font-mono text-[10px] text-emerald-700"
                          : "rounded-full bg-red-100 px-2 py-0.5 font-mono text-[10px] text-red-700"
                      }
                    >
                      {ok ? "✓ " : "✗ "}
                      {p}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* notes */}
            <div>
              <label htmlFor="pe-notes" className="block text-xs font-medium text-slate-700">
                {t("admin.prompts.editor.notesLabel")}
              </label>
              <input
                id="pe-notes"
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={2000}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                disabled={mutation.isPending}
              />
            </div>

            {apiError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {apiError}
              </div>
            )}
          </div>

          <footer className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 transition hover:bg-slate-100"
            >
              {t("common.actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={
                canSubmit
                  ? "inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-blue-700"
                  : "inline-flex cursor-not-allowed items-center gap-1.5 rounded-md bg-slate-300 px-3 py-1.5 text-xs font-medium text-white"
              }
            >
              {mutation.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
              {t("admin.prompts.editor.saveDraft")}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
